import { useEffect } from "react";

/**
 * Track the soft keyboard height on Android tablets and iPads and expose it
 * as CSS variable `--keyboard-inset` on `:root`. Dialogs can then use
 *   max-height: calc(92vh - var(--keyboard-inset, 0px));
 * so their content never gets covered when the tablet keyboard slides up.
 *
 * Uses `visualViewport` (Chrome, Safari, Firefox all support). Silently no-ops
 * on browsers without the API — no-op keyboard height means it's fine.
 */
export function useKeyboardInset() {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const apply = () => {
      // The keyboard reduces `visualViewport.height`. Its top edge can also be
      // pushed by things like tab bars, so we take the max of both signals.
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      document.documentElement.style.setProperty(
        "--keyboard-inset",
        `${inset}px`,
      );
    };

    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
      document.documentElement.style.removeProperty("--keyboard-inset");
    };
  }, []);
}
